import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import { Box, darken, Fab, Tooltip, TooltipProps } from '@mui/material';
import { CustomTooltipTypes } from 'models';
import { InfoDangerIcon, QuestionIcon } from 'components/icons';
import { TERTIARY_COLOR } from 'utils/constants';

import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useStyles } from './MuiCustomTooltip.styles';

export type MuiTooltipPositionType =
	| 'bottom-end'
	| 'bottom-start'
	| 'bottom'
	| 'left-end'
	| 'left-start'
	| 'left'
	| 'right-end'
	| 'right-start'
	| 'right'
	| 'top-end'
	| 'top-start'
	| 'top';

export interface CustomTooltipProps extends Omit<TooltipProps, 'children'> {
	type?: CustomTooltipTypes;
	children?: React.ReactElement;
	fabClasses?: string;
	tooltipLabel?: string;
	tooltipClass?: string;
}

export const MuiCustomTooltip = ({
	type = CustomTooltipTypes.info,
	children,
	fabClasses,
	tooltipLabel,
	tooltipClass,
	...props
}: CustomTooltipProps) => {
	const classes = useStyles();
	const [iconColor, setIconColor] = useState(TERTIARY_COLOR);

	const Icon = useMemo(() => {
		if (children) {
			return children;
		}
		switch (type) {
			case CustomTooltipTypes.warning:
				setIconColor(TERTIARY_COLOR);
				return <InfoDangerIcon iconClass={classes.dangerIconPosition} />;
			case CustomTooltipTypes.info:
				setIconColor(TERTIARY_COLOR);
				return <QuestionIcon />;
			case CustomTooltipTypes.significant:
				setIconColor(TERTIARY_COLOR);
				return <CheckCircleOutlineRoundedIcon />;
			case CustomTooltipTypes.notSignificant:
				setIconColor('#f9a825');
				return <ErrorOutlineIcon />;
			case CustomTooltipTypes.significanceNA:
				setIconColor('#808080');
				return <HighlightOffRoundedIcon />;
		}
	}, [children, type]);
	return (
		<Tooltip
			{...props}
			style={{
				flexShrink: 0,
				zIndex: 500,
			}}
		>
			<Box component="span" display="inline-flex" alignItems="center">
				<Fab
					className={cn(fabClasses, {
						[classes.dangerIconWrapper]: type === CustomTooltipTypes.warning,
					})}
					sx={{
						backgroundColor: iconColor,
						'&:hover': {
							backgroundColor: darken(iconColor, 0.05),
						},
					}}
				>
					{Icon}
				</Fab>
				{tooltipLabel && (
					<p className={cn(tooltipClass, classes.tooltipLabel)}>
						{tooltipLabel}
					</p>
				)}
			</Box>
		</Tooltip>
	);
};
